import { useCallback } from 'react'
import { useSwiper } from 'swiper/react'
import type { DateText } from '~/lib/graphql/scalars'
import type { FormattedDate } from '~/stores/calendar/state'
import {
  useChangeMonth,
  useNextMonth,
  useNextMonthEnabled,
  usePrevMonth,
  usePrevMonthEnabled,
} from '~/stores/calendar/state'
import { ScrollButton } from '~/ui/IconButton'

export function Month({ dates }: { dates: FormattedDate[] }) {
  const { prevMonthEnabled, prev } = usePrevMonthSelection()
  const { nextMonthEnabled, next } = useNextMonthSelection()
  return (
    <div className="flex items-center justify-between">
      <ScrollButton
        direction="left"
        onClick={prev}
        disabled={!prevMonthEnabled}
        ariaLabel="前月のカレンダーを見る"
      />
      <SelectedMonth dates={dates} />
      <ScrollButton
        direction="right"
        onClick={next}
        disabled={!nextMonthEnabled}
        ariaLabel="翌月のカレンダーを見る"
      />
    </div>
  )
}

function SelectedMonth({ dates }: { dates: FormattedDate[] }) {
  return (
    <p className="text-lg font-bold text-neutral-800">
      {dates[0].year}年{dates[0].month}月
    </p>
  )
}

function usePrevMonthSelection() {
  const changeMonth = useChangePrevMonth()
  const prevMonth = usePrevMonth()
  const prevMonthEnabled = usePrevMonthEnabled()
  const prev = useCallback(() => {
    if (prevMonth) {
      changeMonth(prevMonth)
    }
  }, [changeMonth, prevMonth])
  return { prevMonthEnabled, prev }
}

function useNextMonthSelection() {
  const changeMonth = useChangeNextMonth()
  const nextMonth = useNextMonth()
  const nextMonthEnabled = useNextMonthEnabled()
  const next = useCallback(() => {
    if (nextMonth) {
      changeMonth(nextMonth)
    }
  }, [changeMonth, nextMonth])
  return { nextMonthEnabled, next }
}

export function useChangeNextMonth() {
  const change = useChangeMonth()
  const swiper = useSwiper()
  return useCallback(
    (month: DateText) => {
      change(month)
      swiper.slideNext()
    },
    [change, swiper]
  )
}

export function useChangePrevMonth() {
  const change = useChangeMonth()
  const swiper = useSwiper()
  return useCallback(
    (month: DateText) => {
      change(month)
      swiper.slidePrev()
    },
    [change, swiper]
  )
}
